import { ActionIcon, Avatar, Box, Group, Loader, Menu, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconBell, IconCoins, IconHelpCircle, IconLogout, IconPencil, IconSettings } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "@/context/UserContext";
import DashboardCard from "./DashboardCard";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import CollaborationTimelineCard, { type CollaborationTimelineCardItem } from "./CollaborationTimelineCard";
import TitleComponent from "@/components/shared/TitleComponent";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { userDetails, pendingRequestCounts, collaborationSidebarCounts, shortlistedIds, logout } = useUserContext();
  const userName = userDetails?.name?.trim() || "User";
  const companyName = userDetails?.company?.legalName || "Company Name";
  const gtid = userDetails?.company?.gtid || "User";
  const [timelineItems, setTimelineItems] = useState<CollaborationTimelineCardItem[]>([]);

  const [loadingTimeline, setLoadingTimeline] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchTimeline = async () => {
      setLoadingTimeline(true);
      try {
        const data = await trpc.collaboration.getDashboardTimeline.query();
        if (!mounted) return;
        setTimelineItems((data || []) as CollaborationTimelineCardItem[]);
      } catch (err) {
        handleTrpcError(err);
        if (!mounted) return;
        setTimelineItems([]);
      } finally {
        if (mounted) setLoadingTimeline(false);
      }
    };

    if (userDetails?.company?.id) {
      fetchTimeline();
    } else {
      setLoadingTimeline(false);
      setTimelineItems([]);
    }

    return () => {
      mounted = false;
    };
  }, [userDetails?.company?.id]);

  return (
    <Box w="100%" maw="100%" style={{ minWidth: 0 }}>
      <TitleComponent
        title={`Welcome! ${userName}`}
        description="Here is your dashboard overview."
        rightSection={
          <Box visibleFrom="sm">
            <Group gap="sm" wrap="nowrap" align="center">
              <ActionIcon size="lg" variant="light" radius="xl" aria-label="Notifications">
                <IconBell size={18} />
              </ActionIcon>
              <Menu shadow="md" width={230} position="bottom-end">
                <Menu.Target>
                  <Avatar radius="xl" size="md" color="blue" style={{ cursor: "pointer" }}>
                    {userName.charAt(0).toUpperCase()}
                  </Avatar>
                </Menu.Target>
                <Menu.Dropdown>
                  <Box px="sm" py={6}>
                    <Group gap={8} wrap="nowrap">
                      <Avatar radius="xl" size="sm" style={{ backgroundColor: "#eef2ff", color: "#1d4ed8" }}>
                        <IconSettings size={14} />
                      </Avatar>
                      <Box style={{ minWidth: 0 }}>
                        <Text size="sm" fw={600} truncate>
                          {companyName}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {gtid}
                        </Text>
                      </Box>
                    </Group>
                  </Box>
                  <Menu.Divider />
                  <Menu.Item leftSection={<IconPencil size={16} />} onClick={() => navigate("/profile/edit")}>
                    Edit Profile
                  </Menu.Item>
                  <Menu.Item leftSection={<IconCoins size={16} />} onClick={() => navigate("/credits")}>
                    Credits
                  </Menu.Item>
                  <Menu.Item leftSection={<IconHelpCircle size={16} />} onClick={() => navigate("/settings")}>
                    Settings
                  </Menu.Item>
                  <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={logout}>
                    Logout
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Box>
        }
      />

      <Box p="lg">
        <Stack gap="lg">
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="md">
            <Box>
              <DashboardCard
                title="Shortlist"
                count={shortlistedIds.size}
                description="Companies you have shortlisted"
                onClick={() => navigate("/shortlist")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Requests Sent"
                count={pendingRequestCounts.sent}
                description="Requests you've sent waiting for response"
                onClick={() => navigate("/requests")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Requests Received"
                count={pendingRequestCounts.received}
                description="Requests you've received from others"
                onClick={() => navigate("/requests")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Active Collaborations"
                count={collaborationSidebarCounts.active}
                description="Ongoing collaborations with partners"
                onClick={() => navigate("/collaborations")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Closed Collaborations"
                count={collaborationSidebarCounts.closed}
                description="Completed or inactive collaborations"
                onClick={() => navigate("/collaborations")}
              />
            </Box>
          </SimpleGrid>

          <Paper p="lg" radius="md" withBorder>
            <Group justify="space-between" align="center" mb="sm">
              <Box>
                <Text fw={700} fz="lg">
                  Collaboration Timeline
                </Text>
                <Text c="dimmed" size="sm">
                  Track the progress of your collaboration steps from both sides.
                </Text>
              </Box>
              <Group gap="lg">
                <Group gap={6}>
                  <Box w={10} h={10} style={{ borderRadius: "50%", backgroundColor: "var(--mantine-color-green-6)" }} />
                  <Text size="sm" c="dimmed">
                    Completed
                  </Text>
                </Group>
                <Group gap={6}>
                  <Box w={10} h={10} style={{ borderRadius: "50%", backgroundColor: "#E17100" }} />
                  <Text size="sm" c="dimmed">
                    Pending
                  </Text>
                </Group>
              </Group>
            </Group>

            {loadingTimeline ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
              </Group>
            ) : timelineItems.length === 0 ? (
              <Text size="sm" c="dimmed">
                No collaboration timeline available yet.
              </Text>
            ) : (
              <Stack gap="sm">
                {timelineItems.map((item) => (
                  <CollaborationTimelineCard key={item.matchId} item={item} />
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}
